// The Promotion review queue, from Admin Actions.
//
// Approving a first submission is the moment billing starts: the member's
// subscription price swaps to the Promotion price. Nothing was charged before
// that, so rejecting costs nobody anything and there is no refund to issue.
//
// The queue holds two kinds of row. A "new" row is a first submission. An
// "edit" row is a change to a Promotion already running — its live card keeps
// showing the approved version until the edit is approved, so a rejection here
// simply throws the staged copy away.
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink } from 'lucide-react';
import { PROMO_PLATFORMS, listPendingPromotions, profileUrl, reviewPromotion, type PendingPromotion, type PromoPlatform } from '../api/promotions';

/** Older rows carry no platform; all of those are Instagram. */
const platformLabel = (p?: PromoPlatform | null) =>
  PROMO_PLATFORMS.find((x) => x.id === (p ?? 'instagram'))?.label ?? 'Instagram';

export default function PendingPromotions({ onClose }: { onClose: () => void }) {
  const [rows, setRows] = useState<PendingPromotion[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<Record<string, string>>({});

  const load = () => {
    listPendingPromotions()
      .then(setRows)
      .catch((e) => setLoadErr(e instanceof Error ? e.message : 'Could not load the queue'));
  };
  useEffect(load, []);

  const review = async (row: PendingPromotion, approve: boolean) => {
    if (busy) return;
    const reason = approve ? undefined : (prompt('Why? The member sees this.') ?? undefined);
    if (!approve && reason === undefined) return;
    setBusy(row.uid);
    setRowErr((m) => ({ ...m, [row.uid]: '' }));
    try {
      await reviewPromotion(row.uid, approve, reason);
      load();
    } catch (err) {
      const msg = (err as { message?: string })?.message?.replace(/^.*?:\s*/, '') || 'Could not review';
      setRowErr((m) => ({ ...m, [row.uid]: msg }));
    } finally {
      setBusy(null);
    }
  };

  return createPortal(
    <div className="frame-modal-backdrop" onClick={onClose}>
      <div className="gift-modal" onClick={(e) => e.stopPropagation()}>
        <button className="gift-modal-close" onClick={onClose} aria-label="Close"><X size={18} /></button>
        <div className="code-table-head" style={{ marginBottom: 12 }}>
          <span>Pending Featured</span>
          {rows && <span className="code-table-count">{rows.length}</span>}
        </div>

        {loadErr ? (
          <span className="gift-modal-error">{loadErr}</span>
        ) : !rows ? (
          <span className="code-table-empty">Loading…</span>
        ) : rows.length === 0 ? (
          <span className="code-table-empty">Nothing waiting.</span>
        ) : (
          <div className="promo-queue">
            {rows.map((row) => {
              // An edit row is reviewed on its staged copy, not what is live.
              const shown = row.kind === 'edit' && row.pending ? row.pending : row;
              return (
                <div className="promo-queue-row" key={row.uid}>
                  <div className="promo-queue-top">
                    <span className="promo-queue-name">{shown.name}</span>
                    <span className={`promo-queue-kind promo-queue-kind--${row.kind}`}>
                      {row.kind === 'edit' ? 'EDIT' : 'NEW'}
                    </span>
                  </div>
                  <div className="promo-queue-line">
                    <a href={profileUrl(shown.handle, shown.platform)} target="_blank" rel="noopener noreferrer">@{shown.handle}</a>
                    {` on ${platformLabel(shown.platform)}`}{row.email ? ` · ${row.email}` : ''}
                  </div>
                  <div className="promo-queue-line">“{shown.blurb}”</div>
                  {shown.url ? (
                    <a className="promo-queue-url" href={shown.url} target="_blank" rel="noopener noreferrer">
                      {shown.url} <ExternalLink size={12} />
                    </a>
                  ) : (
                    <div className="promo-queue-line promo-queue-line--dim">No link — lands on their Gnosis page.</div>
                  )}
                  {rowErr[row.uid] && <span className="gift-modal-error">{rowErr[row.uid]}</span>}
                  <div className="promo-queue-actions">
                    <button type="button" className="promo-approve" disabled={busy === row.uid} onClick={() => review(row, true)}>
                      {busy === row.uid ? '…' : 'Approve and publish'}
                    </button>
                    <button type="button" className="promo-reject" disabled={busy === row.uid} onClick={() => review(row, false)}>
                      Reject
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>,
    // Over the phone frame rather than the whole viewport, like every other
    // modal — a fixed backdrop on document.body escapes the frame on desktop.
    document.querySelector('.phone-frame') ?? document.body,
  );
}
